import type { ChatSession } from '../types';
import { saveSessionsToIndexedDB, loadSessionsFromIndexedDB } from './sessionIndexedDB';

const ANONYMOUS_SCOPE = 'anonymous';
const PERSONAL_ORG_SCOPE = 'personal';
const CHAT_SESSIONS_PREFIX = 'chat_sessions';
const LAST_ACTIVE_SESSION_PREFIX = 'last_active_session_id';

// ── 存储策略 ──────────────────────────────────────────────
// localStorage：只存会话索引（id / title / updatedAt 等元信息）
// IndexedDB：  完整会话数据（messages / pipeline / planReview 等）
//
// localStorage 索引 ~200B/条，永不超配额 (5-10MB)。
// IndexedDB 配额 50MB-250MB+，可以存放大量重型数据。
//
// 已登录用户额外有服务端备份，IndexedDB 作为离线缓存。
// ──────────────────────────────────────────────────────────

const MAX_SESSIONS_TO_KEEP = 8;
const MAX_SESSION_AGE_DAYS = 30;
const MAX_SESSION_AGE_MS = MAX_SESSION_AGE_DAYS * 24 * 60 * 60 * 1000;

// ── 会话索引（localStorage 存储格式）───────────────
// 只保留渲染侧边栏所需的最小字段

interface SessionIndexItem {
  id: string;
  title: string;
  updatedAt?: number;
  status?: string;
  isStreaming?: boolean;
  lastProvider?: string;
  lastModel?: string;
  messageCount: number;
  lastUserMessage?: string;
  isStarred?: boolean;
}

function toSessionIndex(session: ChatSession): SessionIndexItem {
  const lastUserMsg = [...(session.messages || [])]
    .reverse()
    .find(m => m.role === 'user');

  return {
    id: session.id,
    title: session.title || '',
    updatedAt: session.updatedAt,
    status: session.status,
    isStreaming: session.isStreaming,
    lastProvider: session.lastProvider,
    lastModel: session.lastModel,
    messageCount: (session.messages || []).length,
    lastUserMessage: lastUserMsg?.content?.slice(0, 120),
    isStarred: session.isStarred,
  };
}

// ────────────────────────────────────────────────────────────

export type ChatStorageUser = {
  id?: string | null;
  organizationId?: string | null;
};

export function getChatStorageScope(user: ChatStorageUser | null | undefined): string {
  const userId = typeof user?.id === 'string' ? user.id.trim() : '';
  if (!userId) {
    return ANONYMOUS_SCOPE;
  }

  const organizationId = typeof user?.organizationId === 'string' && user.organizationId.trim()
    ? user.organizationId.trim()
    : PERSONAL_ORG_SCOPE;
  return `${userId}:${organizationId}`;
}

export function getChatSessionsStorageKey(scope: string): string {
  return `${CHAT_SESSIONS_PREFIX}:${scope || ANONYMOUS_SCOPE}`;
}

export function getLastActiveSessionStorageKey(scope: string): string {
  return `${LAST_ACTIVE_SESSION_PREFIX}:${scope || ANONYMOUS_SCOPE}`;
}

/**
 * 从 localStorage 加载会话索引（同步，用于首屏快速渲染侧边栏）。
 * 返回的会话对象只包含元信息和空 messages 数组，
 * 调用方随后应通过 enrichSessionsFromIndexedDB() 补全完整数据。
 */
export function loadScopedChatSessions(
  storage: Storage | undefined,
  scope: string,
  normalizeSessions: (sessions: ChatSession[]) => ChatSession[],
): ChatSession[] {
  if (!storage) {
    return [];
  }

  try {
    const raw = storage.getItem(getChatSessionsStorageKey(scope));
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];

    // localStorage 存的是索引格式，转换回 ChatSession（messages 为空或仅含索引元信息）
    const sessions = parsed.map((item: SessionIndexItem) => ({
      id: item.id,
      title: item.title || '',
      updatedAt: item.updatedAt,
      status: item.status,
      isStreaming: item.isStreaming,
      lastProvider: item.lastProvider,
      lastModel: item.lastModel,
      isStarred: item.isStarred,
      messages: [] as ChatSession['messages'],
      _fromIndex: true as const,
    } as ChatSession));

    return normalizeSessions(sessions);
  } catch {
    return [];
  }
}

/**
 * 保存会话：索引到 localStorage（同步），完整数据到 IndexedDB（异步）。
 */
export function saveScopedChatSessions(
  storage: Storage | undefined,
  scope: string,
  sessions: ChatSession[],
): void {
  if (!storage) {
    return;
  }

  // 保留策略：
  // - 星标会话：永久保留，不计入上限
  // - 非星标会话：最近 N 条 + 30 天内
  const starred = sessions.filter(s => s.isStarred);
  const cutoff = Date.now() - MAX_SESSION_AGE_MS;
  const remainingSlots = Math.max(0, MAX_SESSIONS_TO_KEEP - starred.length);
  const recentUnstarred = sessions
    .filter(s => !s.isStarred && (s.updatedAt || 0) >= cutoff)
    .slice(0, remainingSlots);
  const recent = [...starred, ...recentUnstarred];

  // ── localStorage：只写索引 ──
  const index = recent.map(toSessionIndex);
  const key = getChatSessionsStorageKey(scope);

  try {
    storage.setItem(key, JSON.stringify(index));
  } catch (err) {
    if (!(err instanceof DOMException && err.name === 'QuotaExceededError')) {
      throw err;
    }
    // 连索引都写不下（极端情况）→ 只保留最近 3 条
    try {
      storage.setItem(key, JSON.stringify(index.slice(0, 3)));
    } catch {
      // 彻底放弃，IndexedDB 仍会正常写入
    }
  }

  // ── IndexedDB：异步写入完整数据 ──
  saveSessionsToIndexedDB(recent).catch(() => {
    // 静默失败——下次保存时会重试
  });
}

/**
 * 从 IndexedDB 加载完整会话数据，与 localStorage 索引合并。
 * 索引中已存在的会话直接用 IndexedDB 数据替换，
 * 索引中存在但 IndexedDB 中缺失的会话保持不变（消息为空，需从服务端获取）。
 */
export async function enrichSessionsFromIndexedDB(
  indexSessions: ChatSession[],
): Promise<ChatSession[]> {
  try {
    const fullSessions = await loadSessionsFromIndexedDB();
    if (!fullSessions.length) return indexSessions;

    const fullById = new Map(fullSessions.map(s => [s.id, s]));

    return indexSessions.map(indexSession => {
      const full = fullById.get(indexSession.id);
      return full ?? indexSession;
    });
  } catch {
    return indexSessions;
  }
}

export function getScopedLastActiveSessionId(storage: Storage | undefined, scope: string): string | null {
  return storage?.getItem(getLastActiveSessionStorageKey(scope)) ?? null;
}

export function setScopedLastActiveSessionId(
  storage: Storage | undefined,
  scope: string,
  sessionId: string | null,
): void {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(getLastActiveSessionStorageKey(scope), sessionId || 'null');
  } catch (err) {
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      return;
    }
    throw err;
  }
}

function isTimestampInRange(value: unknown, startMs: number, endMs: number): boolean {
  return typeof value === 'number'
    && Number.isFinite(value)
    && value >= startMs
    && value <= endMs;
}

export function purgeStoredChatSessionsByDateRange(
  storage: Storage | undefined,
  startMs: number,
  endMs: number,
): void {
  if (!storage || !Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs > endMs) {
    return;
  }

  const sessionKeys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(`${CHAT_SESSIONS_PREFIX}:`)) {
      sessionKeys.push(key);
    }
  }

  for (const key of sessionKeys) {
    try {
      const parsed = JSON.parse(storage.getItem(key) || '[]');
      if (!Array.isArray(parsed)) {
        continue;
      }

      const nextIndex = (parsed as SessionIndexItem[]).filter(item => {
        if (isTimestampInRange(item.updatedAt, startMs, endMs)) return false;
        return true;
      });

      storage.setItem(key, JSON.stringify(nextIndex));
    } catch {
      continue;
    }
  }
}

/**
 * A cached conversation that was written down mid-stream.
 *
 * The browser keeps conversations in IndexedDB and opens them from there: a
 * session that already has messages is never re-fetched. That is the right
 * default for a finished conversation and wrong for this one — a row saved
 * while a turn was still being written keeps whatever had not arrived yet
 * missing, permanently. The clarification cards are the visible casualty: the
 * server had all twelve, the cached copy had none, and the turn read as having
 * simply stopped with no way to continue.
 *
 * Two tells, because the first one is not always there.
 *
 * The snapshot may still claim to be streaming: a stream cannot outlive the
 * page that was reading it, so a restored session saying it is mid-flight is
 * saying it was interrupted. That caught the first outage.
 *
 * It did not catch the second, on 2026-09-15: the same mangled row, the same
 * thirteen questions missing, but `isStreaming` had already been cleared on
 * both the session and the message. A snapshot's account of itself is only
 * worth what the writer put in it. So the second tell asks what the turn is
 * missing instead, which does not depend on the writer getting a flag right —
 * an assistant turn with nothing in it that renders was never finished, and a
 * page of token counts and progress state is not something that renders.
 */
interface CachedMessageShape {
  role?: string;
  content?: string;
  isStreaming?: boolean;
  parsed?: unknown;
  clarificationChecklist?: unknown[];
  clarifyChip?: unknown;
  progressState?: { phase?: string };
}

/** The phases a turn can rest at. Anything else is a turn still on its way. */
const TERMINAL_PHASES = new Set(['completed', 'failed']);

/**
 * A turn that stopped streaming somewhere short of an ending.
 *
 * The third tell, and the first that does not ask what the turn renders.
 *
 * 2026-09-15 18:51 线上：两条判据都放行了一份半截快照。`isStreaming` 早已被
 * 清掉，而这一轮确实有正文——需求分析那一整段——所以 `isEmptyAssistantTurn`
 * 第一行就返回 false。服务端与缓存的差是这样的：
 *
 *   clarificationChecklist   12 项       字段不存在
 *   progressState.phase      completed   planning
 *   stageBuffers             多 requirementConfirmation
 *   字段数                    29          29
 *
 * 那段正文写着「下面的确认项已全部按失效安全默认预选，直接确认即可」，而一张
 * 卡片都没有。用户没有可点的东西，刷新也救不回来——判断器说缓存可信。
 * 有正文不等于完整。
 *
 * 这一条问的是写下快照的人有没有自相矛盾：一个流不再 streaming，却停在既非
 * completed 也非 failed 的阶段，这两件事不可能同时为真。它不依赖这一轮该长成
 * 什么样，所以下一个「少了别的东西」的形状也会被它抓住。
 *
 * 没有 progressState 的旧消息不算——那是这个字段存在之前写下的，不是证据。
 */
function stoppedShortOfAnEnding(message: CachedMessageShape): boolean {
  if (message.role !== 'assistant') return false;
  if (message.isStreaming) return false;
  const phase = message.progressState?.phase;
  if (!phase) return false;
  return !TERMINAL_PHASES.has(phase);
}

/**
 * An assistant turn carrying none of the four things a turn is read for: its
 * prose, its generated program, its confirmation cards, or a single card
 * streamed on its own.
 *
 * Deliberately not counted: `pipeline`, `generationMetrics`, `progressState`
 * and the rest of the run record. Those are what the mangled row was full of —
 * counting them as substance is exactly how it passed for a finished turn.
 */
function isEmptyAssistantTurn(message: CachedMessageShape): boolean {
  if (message.role !== 'assistant') return false;
  if (message.content?.trim()) return false;
  if (message.clarificationChecklist?.length) return false;
  if (message.clarifyChip) return false;
  if (message.parsed && Object.keys(message.parsed).length > 0) return false;
  return true;
}

export function isMidStreamSnapshot<T extends CachedMessageShape>(session: {
  isStreaming?: boolean;
  messages?: T[];
}): boolean {
  const messages = session.messages || [];
  // No messages is a cache miss, not a half-written turn. The caller already
  // fetches for that, and calling it a snapshot here would say the cache is
  // damaged when it is merely empty.
  if (messages.length === 0) return false;
  if (session.isStreaming) return true;
  return messages.some(message => Boolean(message.isStreaming)
    || isEmptyAssistantTurn(message)
    || stoppedShortOfAnEnding(message));
}

/**
 * Whether a session restored from the cache has to be fetched before it is shown.
 *
 * One predicate because there are two ways in — the user clicking a row in the
 * sidebar, and the app restoring the last active conversation on load — and the
 * two had drifted. Clicking asked `isMidStreamSnapshot`; restoring asked only
 * whether the cache was empty. So the check that exists to catch a half-written
 * turn was never put to the path most people arrive by.
 *
 * 2026-09-15 23:31 线上：服务端 13 张确认卡、phase `completed`；缓存 0 张、
 * phase `planning`、isStreaming false。刷新页面救不回来，因为刷新走的是只问了
 * 半句的那一条。
 *
 * The caller still has to skip this while it is the one generating the turn —
 * a live stream is writing that cache on purpose. On a fresh load nothing is
 * generating yet, so there is nothing to skip.
 */
export function shouldHydrateRestoredSession<T extends CachedMessageShape>(session: {
  isStreaming?: boolean;
  messages?: T[];
}): boolean {
  return !session.messages?.length || isMidStreamSnapshot(session);
}
